import React from 'react';
import { createPortal } from 'react-dom';
import {
  AdminTrade,
  AdminTradeItem,
  useAdminRemoveCardFromTradeMutation,
} from 'store/services/trade/tradeApi';
import { Points } from 'components/Points/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { toast } from 'react-toastify';

interface Props {
  trade: AdminTrade;
  item: AdminTradeItem;
  onClose: () => void;
}

export default function AdminRemoveCardConfirmModal({ trade, item, onClose }: Props) {
  const [removeCard, { isLoading }] = useAdminRemoveCardFromTradeMutation();

  const handleRemove = async () => {
    try {
      const result = await removeCard({
        tradeId: trade.id,
        itemId: item.id,
      }).unwrap();

      const refunded = result.refundedPoints ?? item.pointValue;
      toast.success(`${item.name} removed from trade. ${refunded} points refunded to receiver.`);
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to remove card. Please try again.');
    }
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="admin-remove-card-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-remove-modal-header">
          <h3 className="admin-remove-modal-title">Remove Card from Trade</h3>
          <button className="admin-remove-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="admin-remove-modal-content">
          <p className="admin-remove-modal-warning">
            Are you sure you want to remove this card from Trade #{trade.tradeNumber}?
          </p>
          <div className="admin-remove-modal-card-info">
            <div className="admin-remove-modal-card-row">
              <span className="admin-remove-modal-label">Card:</span>
              <span>{item.name}</span>
            </div>
            <div className="admin-remove-modal-card-row">
              <span className="admin-remove-modal-label">Set:</span>
              <span>{item.set} ({(item.setCode || '').toUpperCase()})</span>
            </div>
            <div className="admin-remove-modal-card-row">
              <span className="admin-remove-modal-label">Quantity:</span>
              <span>{item.quantity}</span>
            </div>
            <div className="admin-remove-modal-card-row">
              <span className="admin-remove-modal-label">Value:</span>
              <Points points={item.pointValue} type="small" />
            </div>
          </div>
          <p className="admin-remove-modal-note">
            The receiver will be refunded the point value of this card.
          </p>
        </div>

        <div className="admin-remove-modal-footer">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onClose}
            isDisabled={isLoading}
          />
          <button
            className="admin-remove-confirm-btn"
            onClick={handleRemove}
            disabled={isLoading}
          >
            {isLoading ? 'Removing...' : 'Remove Card'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
